'use client'

import { useEffect, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { GripVertical, MinusCircle } from 'lucide-react'
import { Reorder, useDragControls } from 'motion/react'
import { z } from 'zod/v4'

import type { RouterOutputs } from '@package/api'
import { Button, Field, FieldError, FieldLabel, FieldSet, Input, Textarea } from '@ui/web/components'
import { tryCatch } from '@helper/utils/core'

import { useSharedTRPC } from '../context'
import { AreYouSureDialog } from '../lib'

const FormSchema = z.object({
  name: z.string().min(1, 'Judul Koleksi harus diisi'),
  description: z.string().optional(),
})

export type MyBookCollectionFormValues = z.infer<typeof FormSchema>

/** A single book row shown in the sortable list when editing a collection. */
export interface MyBookCollectionFormBook {
  bookId: string
  title: string
  author?: string
  coverUrl?: string
}

interface MyBookCollectionFormProps {
  bookIds?: string[]
  /** Ordered books to show in the sortable list (edit mode). When provided, the submitted bookIds are
   * derived from this list's current order/membership instead of the `bookIds` prop. */
  books?: MyBookCollectionFormBook[]
  initialData?: MyBookCollectionFormValues & { myBookCollectionId: string }
  onFinishSubmitting: (
    data:
      | (RouterOutputs['myBookCollection']['createBookCollection'] & { type: 'create' })
      | (RouterOutputs['myBookCollection']['updateBookCollection'] & { type: 'edit' }),
  ) => void
  onCancel: () => void
  /** When true, show AreYouSureDialog on Batal when there are unsaved changes. Default: false */
  withAreYouSureDialog?: boolean
}

/** A single sortable book row. Drag is bound to the grip handle only so the rest of the row
 * (and the page) stays scrollable on touch devices. */
function BookRow({ book, onRemove }: { book: MyBookCollectionFormBook; onRemove: () => void }) {
  const dragControls = useDragControls()

  return (
    <Reorder.Item
      as='div'
      value={book}
      dragListener={false}
      dragControls={dragControls}
      className='srd:flex srd:min-w-0 srd:w-full srd:max-w-full srd:items-center srd:gap-x-3 srd:border-b srd:border-grey-20 srd:bg-neutral-white srd:py-3'
    >
      <div
        role='button'
        aria-label={`Ubah urutan ${book.title}`}
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => dragControls.start(e)}
        className='srd:flex srd:shrink-0 srd:cursor-grab srd:items-center srd:p-1 srd:text-grey-50 srd:active:cursor-grabbing'
      >
        <GripVertical className='srd:size-5' />
      </div>

      <div className='srd:h-[64px] srd:w-[52px] srd:shrink-0 srd:overflow-clip srd:rounded-md srd:bg-blue-20'>
        {book.coverUrl && (
          <img src={book.coverUrl} alt='' draggable={false} className='srd:h-full srd:w-full srd:object-cover' />
        )}
      </div>

      <div className='srd:flex srd:min-w-0 srd:flex-1 srd:flex-col'>
        <p className='srd:font-semibold srd:text-neutral-black srd:line-clamp-2'>{book.title}</p>
        {book.author && <p className='srd:text-sm srd:text-grey-70 srd:line-clamp-2'>{book.author}</p>}
      </div>

      <Button
        type='button'
        variant='ghost'
        baseButton
        aria-label={`Hapus ${book.title} dari kompilasi`}
        onClick={onRemove}
        className='srd:size-fit srd:shrink-0 srd:rounded-full srd:p-0 srd:text-grey-70 srd:hover:bg-transparent srd:hover:text-main-red-100'
      >
        <MinusCircle className='srd:size-6' />
      </Button>
    </Reorder.Item>
  )
}

export function MyBookCollectionForm({
  bookIds,
  books,
  initialData,
  onFinishSubmitting,
  onCancel,
  withAreYouSureDialog = false,
}: MyBookCollectionFormProps) {
  const trpc = useSharedTRPC()
  const [openAreYouSure, setOpenAreYouSure] = useState(false)
  const [orderedBooks, setOrderedBooks] = useState<MyBookCollectionFormBook[]>(books ?? [])

  const showBookList = books !== undefined
  const initialBookOrder = (books ?? []).map((b) => b.bookId).join(',')
  const currentBookOrder = orderedBooks.map((b) => b.bookId).join(',')
  const hasBookChanges = showBookList && currentBookOrder !== initialBookOrder

  /** Edit mode derives bookIds from the sortable list; create mode uses the `bookIds` prop. */
  const submittedBookIds = showBookList ? orderedBooks.map((b) => b.bookId) : bookIds

  const { mutateAsync: createMyCollection } = useMutation(trpc.myBookCollection.createBookCollection.mutationOptions())
  const { mutateAsync: editMyCollection } = useMutation(trpc.myBookCollection.updateBookCollection.mutationOptions())

  const form = useForm({
    defaultValues: {
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
    } as z.input<typeof FormSchema>,
    validators: {
      onSubmit: FormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, err] = await tryCatch(
        (async () => {
          if (!initialData) {
            const response = await createMyCollection({
              name: value.name,
              description: value.description,
              bookIds: submittedBookIds,
            })
            formApi.reset()
            onFinishSubmitting({ ...response, type: 'create' })
          } else {
            const response = await editMyCollection({
              id: initialData.myBookCollectionId,
              name: value.name,
              description: value.description ?? null,
              bookIds: submittedBookIds,
            })
            onFinishSubmitting({ ...response, type: 'edit' })
          }
        })(),
      )

      if (err) {
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit: err instanceof Error ? err.message : 'Terjadi kesalahan',
        })
      }
    },
  })

  useEffect(() => {
    void form.validateAllFields('change')
  }, [])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const values = useStore(form.store, (state) => state.values)

  const hasUnsavedChanges =
    values.name !== (initialData?.name ?? '') ||
    values.description !== (initialData?.description ?? '') ||
    hasBookChanges

  return (
    <>
      <form
        id='create-my-collection-form'
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
        className='srd:flex srd:min-w-0 srd:w-full srd:flex-col srd:overflow-x-hidden'
      >
        <FieldSet className='srd:flex srd:min-w-0 srd:w-full srd:flex-col srd:gap-y-4' disabled={isSubmitting}>
          <form.Field name='name'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field className='srd:w-full' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Nama kompilasi</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    placeholder='Contoh: Bacaan Hari Minggu'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <form.Field name='description'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field className='srd:w-full' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Deskripsi</FieldLabel>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    placeholder='Tuliskan deskripsi tentang Koleksi ini'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    className='srd:min-w-0'
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>
        </FieldSet>

        {showBookList && orderedBooks.length > 0 && (
          <div className='srd:mt-4 srd:min-w-0 srd:max-h-[min(45dvh,360px)] srd:overflow-x-hidden srd:overflow-y-auto'>
            <Reorder.Group
              as='div'
              axis='y'
              values={orderedBooks}
              onReorder={setOrderedBooks}
              className='srd:flex srd:min-w-0 srd:w-full srd:flex-col'
            >
              {orderedBooks.map((book) => (
                <BookRow
                  key={book.bookId}
                  book={book}
                  onRemove={() => setOrderedBooks((prev) => prev.filter((b) => b.bookId !== book.bookId))}
                />
              ))}
            </Reorder.Group>
          </div>
        )}

        {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' && (
          <p className='srd:text-xs srd:text-justify srd:text-destructive srd:mt-2'>{errorMap.onSubmit}</p>
        )}

        <div className='srd:mt-6 srd:flex srd:min-w-0 srd:w-full srd:shrink-0 srd:flex-wrap srd:items-center srd:justify-end srd:gap-2'>
          <Button
            type='button'
            variant='outline'
            baseButton
            onClick={() => (withAreYouSureDialog && hasUnsavedChanges ? setOpenAreYouSure(true) : onCancel())}
            disabled={isSubmitting}
          >
            {initialData ? 'Kembali' : 'Batal'}
          </Button>
          <Button baseButton loading={isSubmitting} type='submit' disabled={!canSubmit}>
            {initialData ? 'Perbarui' : 'Buat Koleksi'}
          </Button>
        </div>
      </form>

      {withAreYouSureDialog && (
        <AreYouSureDialog
          open={openAreYouSure}
          onOpenChange={setOpenAreYouSure}
          title={initialData ? 'Batalkan perubahan koleksi?' : 'Batalkan pembuatan koleksi?'}
          description={
            initialData ? 'Perubahan yang belum disimpan akan dibatalkan.' : 'Data yang sudah diinput akan dibatalkan.'
          }
          onConfirm={() => {
            form.reset()
            setOpenAreYouSure(false)
            onCancel()
          }}
        />
      )}
    </>
  )
}
